/**
 * Individual file row component with download/delete actions.
 *
 * Blueprint: specs/013-file-services/spec.md
 */

import React, { useState } from 'react';
import { FileIcon } from './FileIcon';
import type { FileInfo } from '../../hooks/useFileList';

interface FileItemProps {
  file: FileInfo;
  onDownload: (path: string, filename: string) => void;
  onDelete: (path: string) => Promise<boolean>;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (path: string) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

export function FileItem({
  file,
  onDownload,
  onDelete,
  selectable = false,
  selected = false,
  onToggleSelect,
}: FileItemProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(file.path);
    } finally {
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };

  const handleDownload = () => {
    if (file.isDir) return;
    onDownload(file.path, file.name);
  };

  return (
    <div
      className={`group flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
        selected ? 'bg-sky-50 dark:bg-sky-900/20 ring-1 ring-sky-200 dark:ring-sky-800' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
      }`}
      data-testid="file-item"
      data-file-path={file.path}
    >
      {selectable && (
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect?.(file.path)}
          className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-200"
          aria-label={`Select ${file.name}`}
        />
      )}
      <FileIcon filename={file.name} mimeType={file.mimeType} className="w-5 h-5 flex-shrink-0" />

      <div className="flex-1 min-w-0">
        {file.isDir ? (
          <span
            className="block w-full text-left text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
            title={file.name}
          >
            {file.name}/
          </span>
        ) : (
          <button
            onClick={handleDownload}
            className="block w-full text-left text-sm font-medium text-gray-900 dark:text-gray-200 truncate hover:text-sky-600 transition-colors"
            title={`Download ${file.name}`}
            data-testid="file-download-link"
          >
            {file.name}
          </button>
        )}
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          {file.isDir ? (
            <span>Folder</span>
          ) : (
            <>
              <span>{formatFileSize(file.size)}</span>
              <span>·</span>
              <span>{formatDate(file.modifiedAt)}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
        {showConfirm ? (
          <>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors text-xs font-medium"
              data-testid="file-delete-confirm"
            >
              {isDeleting ? '...' : 'Delete'}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-xs"
              data-testid="file-delete-cancel"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            {!file.isDir && (
              <button
                onClick={handleDownload}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/30 rounded transition-colors"
                title="Download"
                data-testid="file-download-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setShowConfirm(true)}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
              title="Delete"
              data-testid="file-delete-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
